// 이 기기에 무엇을 알릴까(3차 F13). 슬랙의 "알림 받을 항목"과 같은 세 갈래에 알림음 하나.
//
// 기기의 것이다 — 데스크톱 알림은 이 창이 띄우고, 폰 푸시는 서버가 이미 멘션·DM 만 보낸다
// (AGENTS.md 「모바일 푸시」). 그래서 다크 모드처럼 `platform.keep` 에 둔다. 서버에 두면 "폰에는 모든 새 메시지"
// 처럼 지킬 수 없는 약속을 적게 된다.
//
// 채널마다 정한 단계(`notify`: all·mentions·none)가 이것보다 좁으면 그쪽이 이긴다 — 음소거한 채널이
// "모든 새 메시지" 로 다시 울리면 음소거가 아니다.

import type { Keeper } from '@maru/platform'
import type { ChannelId, Event, UserId } from '@maru/protocol'
import type { NotifyScope } from '@maru/ui'

export const SCOPE_KEY = 'maru.notify.scope'
export const SOUND_KEY = 'maru.notify.sound'

export function read_scope(raw: string | null): NotifyScope {
  return raw === 'all' || raw === 'none' ? raw : 'mentions'
}

export async function load_notify(keep: Keeper): Promise<{ scope: NotifyScope; sound: boolean }> {
  try {
    const [scope, sound] = await Promise.all([keep.get(SCOPE_KEY), keep.get(SOUND_KEY)])
    return { scope: read_scope(scope), sound: sound !== 'off' }
  } catch {
    return { scope: 'mentions', sound: true }
  }
}

/** 저장은 던질 수 있다 — 알림 범위는 편의라 못 적으면 이번 창에서만 산다. 기본값이면 지운다. */
export async function keep_notify(
  keep: Keeper,
  v: { scope: NotifyScope; sound: boolean },
): Promise<void> {
  try {
    if (v.scope === 'mentions') await keep.remove(SCOPE_KEY)
    else await keep.put(SCOPE_KEY, v.scope)
    if (v.sound) await keep.remove(SOUND_KEY)
    else await keep.put(SOUND_KEY, 'off')
  } catch {
    // 이번 창에서만 산다.
  }
}

type Level = 'all' | 'mentions' | 'none'

/**
 * 이 사건을 데스크톱 알림으로 띄울 만한가. 창이 보이는지는 부르는 쪽이 본다.
 * - 끔이면 아무것도.
 * - 채널을 `none` 으로 껐으면 아무것도.
 * - 멘션·따르는 스레드 답글·내 글에 달린 남의 반응은 늘(반응은 서버가 이미 묶어 보낸다).
 * - 남이 쓴 새 줄은 대화방(DM)이면 늘, 채널이면 "모든 새 메시지" 이고 채널 단계도 `all` 일 때만.
 * - 내가 쓴 줄은 **나와의 대화**(`is_note`)에서만 — 리마인더가 거기에 내 이름으로 온다. 안 울리면
 *   `/remind` 는 때가 되어도 아무 말이 없다(3차 F153). 창이 안 보일 때 거기 쓰는 사람은 없다.
 */
export function wants_alert(
  e: Event,
  scope: NotifyScope,
  level_of: (channel: ChannelId) => Level,
  is_dm: (channel: ChannelId) => boolean,
  me: UserId,
  is_note: (channel: ChannelId) => boolean = () => false,
): e is Extract<Event, { t: 'mentioned' | 'thread_replied' | 'reaction_received' | 'message' }> {
  if (scope === 'none') return false
  if (
    e.t !== 'mentioned' &&
    e.t !== 'thread_replied' &&
    e.t !== 'reaction_received' &&
    e.t !== 'message'
  )
    return false
  const level = level_of(e.channel)
  if (level === 'none') return false
  if (e.t !== 'message') return true
  if (e.author === me) return is_note(e.channel)
  if (is_dm(e.channel)) return true
  return scope === 'all' && level === 'all'
}
